import React from 'react';
import { Box, Chip, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { formatMinutes } from '../../utils/effortParser';
import { cardSx, cardTitleSx, INK, LINE, MUTED } from '../home/homeTheme';

const DEFAULT_EFFORT_TYPE_ID = 1;
const COLUMNS = 'minmax(0,2fr) minmax(0,1.4fr) minmax(0,1.1fr) minmax(0,2.4fr) 76px 76px';

/** Worklog registrati nel giorno, con modifica ed eliminazione. */
export default function DayWorklogTable({ worklogs, loading, error, dayLabel, locked, lockMessage, onEdit, onDelete }) {
  const total = worklogs.reduce((sum, w) => sum + (Number(w.effort) || 0), 0);

  return (
    <Box sx={{ ...cardSx, height: 'auto', p: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', px: { xs: 2, md: 2.5 }, pt: 2, pb: 1.25 }}>
        <Typography sx={{ ...cardTitleSx, flex: 1 }}>Tracked on {dayLabel}</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>{formatMinutes(total)}</Typography>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: COLUMNS,
          gap: 2,
          px: 2.5,
          py: 0.75,
          bgcolor: 'var(--rt-subtle)',
          borderTop: `1px solid ${LINE}`,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        {['Project', 'Activity', 'Ticket', 'Description', 'Effort', ''].map((h, i) => (
          <Typography key={h || i} sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: h === 'Effort' ? 'right' : 'left' }}>
            {h}
          </Typography>
        ))}
      </Box>

      {loading && (
        <Box sx={{ px: 2.5, py: 1.5 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={34} />
          ))}
        </Box>
      )}
      {!loading && error && (
        <Typography sx={{ px: 2.5, py: 2, color: 'var(--rt-danger-strong)', fontSize: 14 }}>{error}</Typography>
      )}
      {!loading && !error && worklogs.length === 0 && (
        <Typography sx={{ px: 2.5, py: 2.5, color: MUTED, fontSize: 14 }}>Nothing tracked on this day yet.</Typography>
      )}

      {!loading &&
        !error &&
        worklogs.map((w) => {
          const approved = w.status_name === 'Approved';
          const rowLocked = locked || approved;
          const reason = approved ? 'Approved worklogs can’t be changed' : lockMessage;
          return (
            <Box
              key={w.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', md: COLUMNS },
                gap: { xs: 0.5, md: 2 },
                alignItems: 'center',
                px: { xs: 2, md: 2.5 },
                py: 1.1,
                borderBottom: `1px solid ${LINE}`,
                '&:last-of-type': { borderBottom: 'none' },
                '&:hover': { bgcolor: 'var(--rt-subtle-2)' },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                  {w.project_name}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12, color: MUTED, display: { md: 'none' } }}>
                  {w.activity_name}
                  {w.ticket_id ? ` · ${w.ticket_id}` : ''}
                </Typography>
              </Box>
              <Typography noWrap sx={{ fontSize: 13, color: INK, display: { xs: 'none', md: 'block' } }}>
                {w.activity_name}
                {Number(w.effort_type_id) !== DEFAULT_EFFORT_TYPE_ID && (
                  <Chip size="small" label={w.effort_type_name} sx={{ ml: 0.75, height: 18, fontSize: 11, bgcolor: 'rgba(239,108,0,0.12)', color: 'var(--rt-warn)' }} />
                )}
              </Typography>
              <Typography noWrap sx={{ fontSize: 13, color: MUTED, display: { xs: 'none', md: 'block' }, fontFamily: w.ticket_id ? 'monospace' : 'inherit' }}>
                {w.ticket_id}
              </Typography>
              <Tooltip title={w.description || ''} placement="top-start" enterDelay={500}>
                <Typography noWrap sx={{ fontSize: 13, color: MUTED, display: { xs: 'none', md: 'block' } }}>
                  {w.description}
                </Typography>
              </Tooltip>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: INK, textAlign: 'right' }}>{formatMinutes(w.effort)}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <Tooltip title={rowLocked ? reason : 'Edit'}>
                  <span>
                    <IconButton size="small" onClick={() => onEdit(w)} disabled={rowLocked}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={rowLocked ? reason : 'Delete'}>
                  <span>
                    <IconButton size="small" onClick={() => onDelete(w)} disabled={rowLocked}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
    </Box>
  );
}
